/* registry.js
   ----------------------------------------------------------------------
   Pages are loaded on demand, one bundle each, instead of all of them
   in the first download (the main bundle was 1.6 MB of JavaScript
   before this, 2026-10-08). Two rules make it safe with the
   pre-rendered HTML:

     1. src/index.js loads the module for the current URL BEFORE it
        hydrates, so the first client render has the real page and
        matches the HTML byte for byte. (React.lazy cannot do this: it
        suspends on first render, and the snapshot HTML carries no
        Suspense markers to hydrate into.)
     2. Navigation inside the app renders nothing for the instant the
        next bundle takes to arrive, then the page. The likely next
        pages are prefetched after load (webpackPrefetch), so the
        bundle is usually already in the cache.

   `pageFor(path)` maps a canonical English path to a module and its
   props; App.js throws at start-up if a path in src/seo/site.mjs has
   no module here. */

import React, { useEffect, useReducer } from "react";
import { PUBLIC_ROUTES } from "../seo/site.mjs";

const LOADERS = {
  home:           () => import(/* webpackChunkName: "p-home",      webpackPrefetch: true */ "./Home"),
  programs:       () => import(/* webpackChunkName: "p-programs",  webpackPrefetch: true */ "./Programs"),
  jma:            () => import(/* webpackChunkName: "p-jma",       webpackPrefetch: true */ "./programs/JapaneseMentalArithmetic"),
  programRoute:   () => import(/* webpackChunkName: "p-program",   webpackPrefetch: true */ "./programs/ProgramRoute"),
  trips:          () => import(/* webpackChunkName: "p-trips",     webpackPrefetch: true */ "./Trips"),
  dubaiWinter:    () => import(/* webpackChunkName: "p-dubai",     webpackPrefetch: true */ "./DubaiWinter"),
  campPage:       () => import(/* webpackChunkName: "p-camp",      webpackPrefetch: true */ "./trips/CampPage"),
  schools:        () => import(/* webpackChunkName: "p-schools",   webpackPrefetch: true */ "./Schools"),
  careerPathways: () => import(/* webpackChunkName: "p-pathways",  webpackPrefetch: true */ "./CareerPathways"),
  workshops:      () => import(/* webpackChunkName: "p-workshops", webpackPrefetch: true */ "./Workshops"),
  uk2026:         () => import(/* webpackChunkName: "p-uk2026" */ "./UK2026"),
  giveLearn:      () => import(/* webpackChunkName: "p-give" */ "./GiveLearn"),
  autumnTerm:     () => import(/* webpackChunkName: "p-autumn" */ "./AutumnTerm"),
  refundPolicy:   () => import(/* webpackChunkName: "p-refund" */ "./RefundPolicy"),
  notFound:       () => import(/* webpackChunkName: "p-404" */ "./NotFound"),
  /* Not a page: the fixed "Register Now" button and its modal, only on
     the Dubai camp routes (src/components/RegisterFloat.jsx). Loaded
     with that page so the main bundle carries no trip data. */
  registerFloat:  () => import(/* webpackChunkName: "c-register" */ "../components/RegisterFloat"),
  privacyPolicy:  () => import(/* webpackChunkName: "p-privacy" */ "./PrivacyPolicy"),
};

const FIXED = {
  "/":                                    "home",
  "/programs":                            "programs",
  "/programs/japanese-mental-arithmetic": "jma",
  "/trips":                               "trips",
  "/trips/dubai-winter":                  "dubaiWinter",
  "/trips/uk-2026":                       "uk2026",
  "/give-and-learn":                      "giveLearn",
  "/autumn-term":                         "autumnTerm",
  "/schools":                             "schools",
  "/career-pathways":                     "careerPathways",
  "/workshops":                           "workshops",
  "/refund-policy":                       "refundPolicy",
  "/privacy-policy":                      "privacyPolicy",
};

/* Which module renders a canonical English path, with its props.
   Program pages (src/data/programPages.js, src/data/programSpecs.js)
   and camp pages (src/data/camps.js) share one module each and get the
   slug; the module itself fails loudly on a slug it does not know. */
export function pageFor(path) {
  if (FIXED[path]) return { page: FIXED[path], props: {} };
  const program = /^\/programs\/([^/]+)$/.exec(path);
  if (program) return { page: "programRoute", props: { slug: program[1] } };
  const camp = /^\/trips\/([^/]+)$/.exec(path);
  if (camp) return { page: "campPage", props: { slug: camp[1] } };
  return null;
}

PUBLIC_ROUTES.forEach(({ path }) => {
  if (!pageFor(path)) throw new Error(`src/seo/site.mjs lists ${path} but src/pages/registry.js has no page for it.`);
});

const loaded = {};
const pending = {};

export function loadPage(page) {
  if (loaded[page]) return Promise.resolve(loaded[page]);
  if (!pending[page]) {
    pending[page] = LOADERS[page]().then((m) => {
      loaded[page] = m.default;
      return m.default;
    });
  }
  return pending[page];
}

/* Extra modules a path needs besides its page (preloaded with it). */
export function extrasFor(path) {
  return path === "/trips/dubai-winter" ? ["registerFloat"] : [];
}

export function LazyPage({ page, ...props }) {
  const [, rerender] = useReducer((n) => n + 1, 0);
  const Component = loaded[page];
  useEffect(() => {
    if (!Component) loadPage(page).then(rerender);
  }, [page, Component]);
  return Component ? <Component {...props} /> : null;
}
